import { saveAs } from 'file-saver';
import JSZip from 'jszip';

const urlType = {
  video: 'video/*',
  image: 'image/*',
};

/**
 * 获取文件名后缀
 * @param fileName
 */
export const getFileExt = (fileName: string) => {
  if (!fileName) return '';
  return fileName.substring(fileName.lastIndexOf('.') + 1);
};

/**
 * 下载文件
 * @param url
 * @param type
 */
export function download(url: string, type: keyof typeof urlType) {
  fetch(url, {
    // cache 会导致图片请求跨域问题
    cache: 'no-cache',
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      response
        .arrayBuffer()
        .then((res) => {
          const blob = new Blob([res], { type: urlType[type] });
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.style.display = 'none';
          a.href = objectUrl;
          a.download = `${Date.now()}.${getFileExt(url)}`;
          a.click();
          URL.revokeObjectURL(objectUrl);
          document.body.removeChild(a);
        })
        .catch((error) => {
          console.error('Error reading array buffer:', error);
        });
    })
    .catch((error) => {
      console.error('Fetch error:', error);
    });
}

/**
 * 从 URL 中提取末段文件名（去掉 query/hash），跨域下载场景下与浏览器默认保存名一致。
 */
const getFilenameFromUrl = (url: string): string => {
  try {
    const u = new URL(url, typeof window === 'undefined' ? 'http://localhost' : window.location.href);
    const last = u.pathname.split('/').pop() || '';
    return last ? decodeURIComponent(last) : '';
  } catch {
    return '';
  }
};

/**
 * 下载文件（支持进度回调）
 * @param url 文件 URL
 * @param type 文件类型
 * @param onProgress 进度回调 (0-100)
 * @param filename 显式文件名；不传则取 URL 末段（与桌面端跨域下载行为一致），仍取不到再退回 时间戳.扩展名
 */
export async function downloadWithProgress(
  url: string,
  type: keyof typeof urlType,
  onProgress?: (percent: number) => void,
  filename?: string,
): Promise<void> {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? Number.parseInt(contentLength, 10) : 0;

  if (!response.body) throw new Error('ReadableStream not supported');

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let receivedLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    receivedLength += value.length;

    if (total > 0 && onProgress) {
      const percent = Math.round((receivedLength / total) * 100);
      onProgress(percent);
    }
  }

  // 合并所有 chunk
  const allChunks = new Uint8Array(receivedLength);
  let position = 0;
  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }

  // 触发下载
  const blob = new Blob([allChunks], { type: urlType[type] });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  a.href = objectUrl;
  a.download = filename || getFilenameFromUrl(url) || `${Date.now()}.${getFileExt(url)}`;
  a.click();
  URL.revokeObjectURL(objectUrl);
  document.body.removeChild(a);
}

/**
 * 下载多个文件为 zip
 * @param files 文件数组，每个文件包含 url 和 name
 * @param options
 */
export const downloadFilesAsZip = async (
  files: Array<{ url: string; name: string }>,
  options?: {
    zipName?: string;
    onBegin?: () => void;
    onSuccess?: () => void;
    onFail?: () => void;
  },
) => {
  const { onBegin, onSuccess, onFail, zipName = 'download.zip' } = options || {};

  onBegin?.();
  const zip = new JSZip();
  const downloadedFiles: Array<{ name: string; blob: Blob }> = [];

  try {
    for (let i = 0; i < files.length; i++) {
      const { url, name } = files[i];
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch from ${url}`);
        }

        const blob = await response.blob();
        if (blob.size === 0) {
          throw new Error(`Empty file from ${url}`);
        }
        downloadedFiles.push({ name, blob });
      } catch (error) {
        console.error(`Error downloading from ${url}:`, error);
        throw error; // 向上抛出错误，中断整个下载过程
      }
    }

    // 只有当所有文件都下载成功后，才创建 zip
    if (downloadedFiles.length === files.length) {
      downloadedFiles.forEach(({ name, blob }) => {
        zip.file(name, blob);
      });

      const content = await zip.generateAsync({ type: 'blob' });
      if (content.size === 0) {
        throw new Error('Generated zip file is empty');
      }

      saveAs(content, zipName);
      onSuccess?.();
    } else {
      throw new Error('Some files failed to download');
    }
  } catch (error) {
    console.error('Error in download process:', error);
    onFail?.();
  }
};

/**
 * 简单下载文件（适用于已有可下载 URL 的场景）
 * @param url 下载链接
 * @param filename 文件名
 * @param options 选项
 */
export const downloadFile = (
  url: string,
  filename: string,
  options?: {
    /** 下载完成后是否自动清理 Blob URL */
    autoRevokeBlobUrl?: boolean;
  },
) => {
  const { autoRevokeBlobUrl = false } = options || {};

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  // 如果是 Blob URL，下载完成后释放内存
  if (autoRevokeBlobUrl && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};
